/**
 * Executive Demo Mock Data
 * ------------------------
 * Pure client-side, read-only mock generators used ONLY when the URL
 * contains `?demo=executive` or the executive-demo localStorage flag is set.
 *
 * NEVER persisted. NEVER inserted into Supabase. NEVER affects production
 * data. Pulled by a handful of P0 surfaces to eliminate the empty-table
 * blockers identified in LAUNCH-READINESS-01.
 */
import type { MundialPredictionEvent } from "@/lib/predictions/mundialEngine";
import type { PollWithOptions } from "@/lib/polls/pollsService";

const STORAGE_KEY = "ss2026_executive_demo";

export function isExecutiveDemoActive(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("demo") === "executive") return true;
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * WORLD-CUP-LAUNCH-P1.
 * Foundation fallback flag.
 *
 * SPRINT-LAUNCH-P0-ENGINE: this flag is now gated to the executive / QA
 * demo surface only (`?demo=executive` or the executive-demo localStorage
 * flag). In production with real users the flag MUST return false so that
 * the Prediction Pulse never silently swaps real (UUID, persisted,
 * settle-able) events for `MOCK_PREDICTION_EVENTS` (string ids `pe-1`…).
 *
 * Mocks are still allowed for editorial-only surfaces (Daily WOW, Nation
 * leaderboards) via their own callers — those do not feed
 * `prediction_answers` / `xp_grant_log` / Nation Wars scoring and are safe
 * to render as foundation content. Prediction events are *not* one of
 * those surfaces.
 */
export function shouldUseFoundationFallback(): boolean {
  return isExecutiveDemoActive();
}

/* ──────────────────────────  NATION WARS  ────────────────────────── */

export interface MockNationScore {
  nation_code: string;
  nation_name: string;
  total_points: number;
  total_predictions: number;
  total_users: number;
  updated_at: string;
}

export const MOCK_NATION_SCORES: MockNationScore[] = [
  { nation_code: "MEX", nation_name: "México",        total_points: 24_580, total_predictions: 18_240, total_users: 6_412, updated_at: new Date().toISOString() },
  { nation_code: "BRA", nation_name: "Brasil",        total_points: 23_110, total_predictions: 17_905, total_users: 5_988, updated_at: new Date().toISOString() },
  { nation_code: "ARG", nation_name: "Argentina",     total_points: 22_870, total_predictions: 17_320, total_users: 5_741, updated_at: new Date().toISOString() },
  { nation_code: "USA", nation_name: "Estados Unidos",total_points: 18_640, total_predictions: 14_125, total_users: 4_902, updated_at: new Date().toISOString() },
  { nation_code: "COL", nation_name: "Colombia",      total_points: 16_420, total_predictions: 12_580, total_users: 4_188, updated_at: new Date().toISOString() },
  { nation_code: "PER", nation_name: "Perú",          total_points: 12_180, total_predictions:  9_640, total_users: 3_205, updated_at: new Date().toISOString() },
  { nation_code: "ESP", nation_name: "España",        total_points: 11_540, total_predictions:  8_910, total_users: 2_984, updated_at: new Date().toISOString() },
  { nation_code: "FRA", nation_name: "Francia",       total_points:  9_820, total_predictions:  7_625, total_users: 2_511, updated_at: new Date().toISOString() },
];

export const MOCK_NATION_RIVALRY_ROWS = MOCK_NATION_SCORES.map((n, i) => ({
  nation_code: n.nation_code,
  hype_total: Math.round(n.total_points * 1.35) + (8 - i) * 250,
  active_fans: Math.round(n.total_users * 0.42) + (8 - i) * 30,
}));

export const MOCK_LIVE_PULSE = {
  active_fans: 47_823,
  hype_burst: 312,
};

/* ──────────────────────────  PREDICTION PULSE  ────────────────────────── */

const MOCK_MATCHES = [
  { id: "match-mex-arg", home: "MEX", away: "ARG" },
  { id: "match-bra-esp", home: "BRA", away: "ESP" },
  { id: "match-usa-fra", home: "USA", away: "FRA" },
  { id: "match-col-per", home: "COL", away: "PER" },
  { id: "match-arg-bra", home: "ARG", away: "BRA" },
];

export const MOCK_UPCOMING_MATCHES = MOCK_MATCHES.map((m, i) => ({
  ...m,
  kickoff: new Date(Date.now() + (i + 1) * 6 * 3600 * 1000).toISOString(),
}));

const now = () => Date.now();

export const MOCK_PREDICTION_EVENTS: MundialPredictionEvent[] = [
  { id: "pe-1",  match_id: "match-mex-arg", event_type: "next_goal",             question: "¿Quién anota el próximo gol?",        option_a: "México",    option_b: "Argentina", option_c: "Sin gol", window_seconds: 60, points_reward: 25, start_time: new Date(now() - 30_000).toISOString(),  end_time: new Date(now() + 90_000).toISOString(),  correct_answer: null, status: "open" },
  { id: "pe-2",  match_id: "match-mex-arg", event_type: "next_corner",           question: "¿Próximo tiro de esquina?",            option_a: "México",    option_b: "Argentina", option_c: null,     window_seconds: 30, points_reward: 50, start_time: new Date(now() - 15_000).toISOString(),  end_time: new Date(now() + 45_000).toISOString(),  correct_answer: null, status: "open" },
  { id: "pe-3",  match_id: "match-bra-esp", event_type: "next_shot_on_target",   question: "¿Quién dispara a puerta primero?",    option_a: "Brasil",    option_b: "España",    option_c: null,     window_seconds: 60, points_reward: 25, start_time: new Date(now() - 20_000).toISOString(),  end_time: new Date(now() + 100_000).toISOString(), correct_answer: null, status: "open" },
  { id: "pe-4",  match_id: "match-bra-esp", event_type: "next_card",             question: "¿Próxima tarjeta?",                    option_a: "Brasil",    option_b: "España",    option_c: "Ninguna",window_seconds: 90, points_reward: 10, start_time: new Date(now() - 10_000).toISOString(),  end_time: new Date(now() + 160_000).toISOString(), correct_answer: null, status: "open" },
  { id: "pe-5",  match_id: "match-usa-fra", event_type: "next_attacking_team",   question: "¿Qué equipo ataca a continuación?",   option_a: "USA",       option_b: "Francia",   option_c: null,     window_seconds: 30, points_reward: 50, start_time: new Date(now() - 5_000).toISOString(),   end_time: new Date(now() + 55_000).toISOString(),  correct_answer: null, status: "open" },
  { id: "pe-6",  match_id: "match-usa-fra", event_type: "free_kick_result",      question: "Tiro libre: ¿resultado?",              option_a: "Gol",       option_b: "Atajada",   option_c: "Fuera",  window_seconds: 60, points_reward: 25, start_time: new Date(now() - 25_000).toISOString(),  end_time: new Date(now() + 95_000).toISOString(),  correct_answer: null, status: "open" },
  { id: "pe-7",  match_id: "match-col-per", event_type: "next_goal",             question: "¿Quién marca primero?",                option_a: "Colombia",  option_b: "Perú",      option_c: "0-0",    window_seconds: 90, points_reward: 10, start_time: new Date(now() - 40_000).toISOString(),  end_time: new Date(now() + 140_000).toISOString(), correct_answer: null, status: "open" },
  { id: "pe-8",  match_id: "match-col-per", event_type: "next_corner",           question: "¿Próximo córner?",                     option_a: "Colombia",  option_b: "Perú",      option_c: null,     window_seconds: 30, points_reward: 50, start_time: new Date(now() - 8_000).toISOString(),   end_time: new Date(now() + 52_000).toISOString(),  correct_answer: null, status: "open" },
  { id: "pe-9",  match_id: "match-arg-bra", event_type: "penalty_result",        question: "Penal: ¿qué pasa?",                    option_a: "Gol",       option_b: "Atajado",   option_c: "Fallado",window_seconds: 60, points_reward: 25, start_time: new Date(now() - 12_000).toISOString(),  end_time: new Date(now() + 108_000).toISOString(), correct_answer: null, status: "open" },
  { id: "pe-10", match_id: "match-arg-bra", event_type: "next_shot_on_target",   question: "Próximo remate al arco",               option_a: "Argentina", option_b: "Brasil",    option_c: null,     window_seconds: 60, points_reward: 25, start_time: new Date(now() - 18_000).toISOString(),  end_time: new Date(now() + 102_000).toISOString(), correct_answer: null, status: "open" },
];

/** Mock vote distribution per event (totals & %). Purely cosmetic for demo. */
export const MOCK_PREDICTION_VOTES: Record<string, { a: number; b: number; c: number; total: number }> = {
  "pe-1":  { a: 1842, b: 2105, c:  312, total: 4259 },
  "pe-2":  { a: 1320, b: 1185, c:    0, total: 2505 },
  "pe-3":  { a: 1890, b: 1402, c:    0, total: 3292 },
  "pe-4":  { a:  712, b:  604, c: 1138, total: 2454 },
  "pe-5":  { a:  998, b: 1340, c:    0, total: 2338 },
  "pe-6":  { a:  420, b: 1810, c:  672, total: 2902 },
  "pe-7":  { a: 1402, b: 1188, c:  480, total: 3070 },
  "pe-8":  { a: 1024, b:  988, c:    0, total: 2012 },
  "pe-9":  { a: 2103, b:  812, c:  398, total: 3313 },
  "pe-10": { a: 1622, b: 1488, c:    0, total: 3110 },
};

/* ──────────────────────────  FAN VOICE (POLLS)  ────────────────────────── */

const baseISO = new Date().toISOString();

function mkOpts(pollId: string, labels: string[]) {
  return labels.map((label, i) => ({
    id: `${pollId}-opt-${i + 1}`,
    poll_id: pollId,
    label,
    sort_order: i,
  }));
}

export const MOCK_LIVE_POLLS: PollWithOptions[] = [
  {
    id: "demo-poll-1", tenant_id: null, title: "MVP del partido", description: null,
    question: "¿Quién será el MVP de México vs Argentina?",
    status: "live", reward_type: "fan_points", reward_value: 50,
    starts_at: baseISO, ends_at: null, countries: [], target_segments: [],
    sponsor: null, created_at: baseISO, updated_at: baseISO,
    options: mkOpts("demo-poll-1", ["Lionel Messi", "Hirving Lozano", "Julián Álvarez", "Edson Álvarez"]),
  },
  {
    id: "demo-poll-2", tenant_id: null, title: "Marcador final", description: null,
    question: "¿Cómo termina Brasil vs España?",
    status: "live", reward_type: "fan_points", reward_value: 30,
    starts_at: baseISO, ends_at: null, countries: [], target_segments: [],
    sponsor: null, created_at: baseISO, updated_at: baseISO,
    options: mkOpts("demo-poll-2", ["Gana Brasil", "Empate", "Gana España"]),
  },
  {
    id: "demo-poll-3", tenant_id: null, title: "Cerveza oficial", description: null,
    question: "¿Con qué cerveza ves el Mundial?",
    status: "live", reward_type: "coupon", reward_value: 1,
    starts_at: baseISO, ends_at: null, countries: [], target_segments: [],
    sponsor: "Demo Brand", created_at: baseISO, updated_at: baseISO,
    options: mkOpts("demo-poll-3", ["Corona", "Modelo", "Heineken", "Quilmes"]),
  },
  {
    id: "demo-poll-4", tenant_id: null, title: "Próximo campeón", description: null,
    question: "¿Quién levanta la copa en 2026?",
    status: "live", reward_type: "fan_points", reward_value: 100,
    starts_at: baseISO, ends_at: null, countries: [], target_segments: [],
    sponsor: null, created_at: baseISO, updated_at: baseISO,
    options: mkOpts("demo-poll-4", ["Argentina", "Brasil", "Francia", "España"]),
  },
  {
    id: "demo-poll-5", tenant_id: null, title: "Momento favorito", description: null,
    question: "¿Qué momento te emociona más?",
    status: "live", reward_type: "fan_points", reward_value: 20,
    starts_at: baseISO, ends_at: null, countries: [], target_segments: [],
    sponsor: null, created_at: baseISO, updated_at: baseISO,
    options: mkOpts("demo-poll-5", ["Goles", "Penales", "Tiros libres", "Festejos"]),
  },
];

/** 200 simulated responses per poll, distributed across options. */
export const MOCK_POLL_RESPONSE_COUNTS: Record<string, number[]> = {
  "demo-poll-1": [62, 48, 54, 36],
  "demo-poll-2": [98, 41, 61],
  "demo-poll-3": [71, 68, 34, 27],
  "demo-poll-4": [82, 54, 38, 26],
  "demo-poll-5": [104, 32, 28, 36],
};

/* ──────────────────────────  NATION PULSES  ────────────────────────── */

export const MOCK_NATION_PULSES = [
  { id: "np-1", nation_code: "MEX", rival_code: "ARG", intensity: 87, overtake: true,  created_at: new Date(Date.now() - 60_000).toISOString() },
  { id: "np-2", nation_code: "BRA", rival_code: "ESP", intensity: 79, overtake: false, created_at: new Date(Date.now() - 120_000).toISOString() },
  { id: "np-3", nation_code: "ARG", rival_code: "BRA", intensity: 92, overtake: true,  created_at: new Date(Date.now() - 180_000).toISOString() },
  { id: "np-4", nation_code: "USA", rival_code: "FRA", intensity: 65, overtake: false, created_at: new Date(Date.now() - 240_000).toISOString() },
  { id: "np-5", nation_code: "COL", rival_code: "PER", intensity: 71, overtake: true,  created_at: new Date(Date.now() - 300_000).toISOString() },
];

/* ──────────────────────────  MEDIA BLITZ — VIRAL CLIPS  ────────────────────────── */

const CLIP_AUTHORS = ["@futbolero_mx", "@argentina10", "@samba_br", "@usa_soccer", "@cafetero_col", "@incaperu", "@laroja_esp", "@lesbleus_fr"];
const CLIP_TITLES = [
  "GOLAZO desde media cancha", "Atajada IMPOSIBLE del portero",
  "Festejo que rompió internet", "El regate que enloqueció al estadio",
  "Tiro libre perfecto", "Penal en último minuto", "Jugada de cinco toques",
  "Tackle decisivo en el área", "Asistencia de lujo", "Bicicleta espectacular",
];

export const MOCK_VIRAL_CLIPS = Array.from({ length: 50 }).map((_, i) => {
  const author = CLIP_AUTHORS[i % CLIP_AUTHORS.length];
  const title = CLIP_TITLES[i % CLIP_TITLES.length];
  const views = 4_200 + Math.round(Math.random() * 38_000);
  return {
    id: `mock-clip-${i + 1}`,
    author_handle: author,
    nation_code: MOCK_NATION_SCORES[i % MOCK_NATION_SCORES.length].nation_code,
    title: `${title} · #${i + 1}`,
    platform: ["tiktok", "instagram", "youtube", "x"][i % 4],
    views,
    likes: Math.round(views * 0.12),
    shares: Math.round(views * 0.04),
    created_at: new Date(Date.now() - i * 6 * 60_000).toISOString(),
  };
});

export const MOCK_MEDIA_BLITZ_TOTALS = {
  clips: MOCK_VIRAL_CLIPS.length,
  views: MOCK_VIRAL_CLIPS.reduce((s, c) => s + c.views, 0),
  likes: MOCK_VIRAL_CLIPS.reduce((s, c) => s + c.likes, 0),
  shares: MOCK_VIRAL_CLIPS.reduce((s, c) => s + c.shares, 0),
};

/* ──────────────────────────  WORLD-CUP-LAUNCH-P2  ──────────────────────────
 * Foundational editorial content. Used as a permanent fallback whenever the
 * live database returns zero rows. Editorial / historical only — never fake
 * users, never fake revenue, never fake conversions.
 * ──────────────────────────────────────────────────────────────────────── */

export interface FoundationDailyWow {
  id: string;
  emoji: string;
  headline: string;
  subline: string;
  nation: string;
  year: number;
}

export const FOUNDATION_DAILY_WOW: FoundationDailyWow[] = [
  { id: "fw-1",  emoji: "🇦🇷", headline: "Maradona vs Inglaterra",  subline: "El gol del siglo · México 1986",        nation: "ARG", year: 1986 },
  { id: "fw-2",  emoji: "🇪🇸", headline: "Iniesta y la gloria",     subline: "Final Sudáfrica 2010",                   nation: "ESP", year: 2010 },
  { id: "fw-3",  emoji: "🇧🇷", headline: "Pelé, Rey del Mundo",     subline: "Triple corona — 1958/1962/1970",         nation: "BRA", year: 1970 },
  { id: "fw-4",  emoji: "🇲🇽", headline: "El estadio que rugió",     subline: "Azteca '86 — la cuna del Mundial",       nation: "MEX", year: 1986 },
  { id: "fw-5",  emoji: "🇫🇷", headline: "Zidane y la magia 98",     subline: "Doblete en la final del Mundial",        nation: "FRA", year: 1998 },
  { id: "fw-6",  emoji: "🇩🇪", headline: "Götze al minuto 113",     subline: "Maracaná decidió la final · 2014",       nation: "DEU", year: 2014 },
  { id: "fw-7",  emoji: "🇦🇷", headline: "Messi levanta la copa",   subline: "Qatar 2022 — final eterna",              nation: "ARG", year: 2022 },
  { id: "fw-8",  emoji: "🇮🇹", headline: "Materazzi y la cabezada", subline: "Berlín 2006 — capítulo final",           nation: "ITA", year: 2006 },
  { id: "fw-9",  emoji: "🇺🇾", headline: "Maracanazo eterno",        subline: "Uruguay calla a Brasil · 1950",          nation: "URU", year: 1950 },
  { id: "fw-10", emoji: "🇰🇷", headline: "El milagro coreano",       subline: "Semifinal en casa · 2002",               nation: "KOR", year: 2002 },
  { id: "fw-11", emoji: "🇳🇱", headline: "La Naranja Mecánica",      subline: "Cruyff y el fútbol total · 1974",        nation: "NLD", year: 1974 },
  { id: "fw-12", emoji: "🇨🇲", headline: "Roger Milla baila",        subline: "Italia '90 — África ataca",              nation: "CMR", year: 1990 },
  { id: "fw-13", emoji: "🇸🇪", headline: "Pelé contra Suecia",       subline: "Final '58 — un niño de 17 años",         nation: "SWE", year: 1958 },
  { id: "fw-14", emoji: "🇨🇿", headline: "Panenka, el penal perfecto", subline: "Eurocopa '76, leyenda mundialista",    nation: "CZE", year: 1976 },
  { id: "fw-15", emoji: "🇨🇴", headline: "Valderrama y el café",     subline: "Colombia ataca el mundo · 1990",         nation: "COL", year: 1990 },
  { id: "fw-16", emoji: "🇸🇳", headline: "Senegal sorprende",        subline: "Debut histórico vs Francia · 2002",      nation: "SEN", year: 2002 },
  { id: "fw-17", emoji: "🇺🇸", headline: "USA vs Inglaterra",        subline: "El milagro de Belo Horizonte · 1950",    nation: "USA", year: 1950 },
  { id: "fw-18", emoji: "🇧🇷", headline: "Ronaldo Fenómeno",         subline: "Corea-Japón 2002 — venganza dulce",      nation: "BRA", year: 2002 },
  { id: "fw-19", emoji: "🇭🇷", headline: "Croacia, generación dorada", subline: "Final Rusia 2018",                    nation: "HRV", year: 2018 },
  { id: "fw-20", emoji: "🇲🇦", headline: "Marruecos rompe records",  subline: "Semifinal histórica · 2022",             nation: "MAR", year: 2022 },
  { id: "fw-21", emoji: "🇵🇹", headline: "Eusébio, la Pantera Negra", subline: "Goleador de Inglaterra '66",            nation: "PRT", year: 1966 },
  { id: "fw-22", emoji: "🇨🇭", headline: "Suiza eliminó a Francia",  subline: "Octavos de Catar 2022",                  nation: "CHE", year: 2022 },
  { id: "fw-23", emoji: "🇧🇪", headline: "Bélgica remonta a Japón",  subline: "Rusia 2018 — minuto 94",                 nation: "BEL", year: 2018 },
  { id: "fw-24", emoji: "🇲🇽", headline: "Hugo Sánchez y la chilena", subline: "Símbolo de una generación",             nation: "MEX", year: 1986 },
  { id: "fw-25", emoji: "🇦🇷", headline: "Goycochea ataja todo",     subline: "Italia '90, héroe inesperado",           nation: "ARG", year: 1990 },
  { id: "fw-26", emoji: "🇧🇷", headline: "Ronaldinho elimina ingleses", subline: "Tiro libre imposible · 2002",         nation: "BRA", year: 2002 },
  { id: "fw-27", emoji: "🇮🇹", headline: "Italia campeona 2006",     subline: "Cuatro estrellas brillan en Berlín",     nation: "ITA", year: 2006 },
  { id: "fw-28", emoji: "🇪🇸", headline: "Tiki-Taka que cambió todo", subline: "España domina el planeta 2010",         nation: "ESP", year: 2010 },
  { id: "fw-29", emoji: "🇳🇬", headline: "Nigeria '94, fútbol arte", subline: "Octavos contra Italia",                  nation: "NGA", year: 1994 },
  { id: "fw-30", emoji: "🌍", headline: "El Mundial nos une",        subline: "Cada 4 años el planeta se detiene",       nation: "ALL", year: 2026 },
];

function dayIndex(seed: string, len: number): number {
  const d = new Date();
  const key = `${seed}-${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return Math.abs(h) % Math.max(1, len);
}

export function pickFoundationDailyWow(seed = "wow"): FoundationDailyWow {
  return FOUNDATION_DAILY_WOW[dayIndex(seed, FOUNDATION_DAILY_WOW.length)];
}

/** Curated cinematic moments rendered when the live engine has nothing yet. */
export interface FoundationCinematicMoment {
  id: string;
  kind: "hype_spike" | "nation_dominance" | "community_shockwave" | "rivalry_surge" | "last_minute_drama";
  emoji: string;
  headlineKey: string;
  subKey?: string;
  tokens?: Record<string, string | number>;
  intensity: number;
  accent: "rose" | "amber" | "cyan" | "violet" | "emerald";
}

export const FOUNDATION_CINEMATIC_MOMENTS: FoundationCinematicMoment[] = [
  { id: "fc-1", kind: "nation_dominance",  emoji: "🇦🇷", headlineKey: "cm_nation_dominance_h", subKey: "cm_nation_dominance_s", tokens: { nation: "ARG", pct: 52 }, intensity: 0.78, accent: "amber" },
  { id: "fc-2", kind: "rivalry_surge",     emoji: "🇲🇽", headlineKey: "cm_nation_dominance_h", subKey: "cm_nation_dominance_s", tokens: { nation: "MEX", pct: 47 }, intensity: 0.72, accent: "amber" },
  { id: "fc-3", kind: "community_shockwave", emoji: "🌎", headlineKey: "cm_shockwave_h",       subKey: "cm_shockwave_s",        tokens: { n: 47823 },               intensity: 0.85, accent: "rose"  },
  { id: "fc-4", kind: "hype_spike",        emoji: "💥", headlineKey: "cm_hype_spike_h",       subKey: "cm_hype_spike_s",       tokens: { n: 312 },                 intensity: 0.66, accent: "rose"  },
];